/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import UiIcon from 'components/Ui/UiIcon'

export type UiStateVariant = 'loading' | 'error' | 'empty'

type UiStateViewProps = {
    variant: UiStateVariant
    title?: React.ReactNode
    description?: React.ReactNode
    action?: React.ReactNode
    icon?: React.ReactNode
    hideIcon?: boolean
    compact?: boolean
    className?: string
}

const UiStateView: React.FC<UiStateViewProps> = ({
    variant,
    title,
    description,
    action,
    icon,
    hideIcon,
    compact,
    className,
}) => {
    const { t } = useTranslation()

    const defaultTitleByVariant: Record<UiStateVariant, string> = {
        loading: t('Loading...'),
        error: t('An unexpected error occurred'),
        empty: t('No data found'),
    }

    const iconNodeByVariant: Record<UiStateVariant, React.ReactNode> = {
        loading: <UiIcon name="loader" className="size-5 animate-spin text-arbo-orange" aria-hidden="true" />,
        error: <UiIcon name="alert-triangle" className="size-5 text-red-500" aria-hidden="true" />,
        empty: <UiIcon name="inbox" className="size-5 text-gray-400" aria-hidden="true" />,
    }

    const resolvedTitle = title ?? defaultTitleByVariant[variant]
    const resolvedIcon = icon ?? iconNodeByVariant[variant]

    const role = variant === 'error' ? 'alert' : 'status'
    const ariaLive = variant === 'error' ? 'assertive' : 'polite'

    if (compact) {
        return (
            <div
                className={clsx('UiStateView flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300', className)}
                role={role}
                aria-live={ariaLive}
            >
                {!hideIcon && resolvedIcon}
                <span className="font-medium">{resolvedTitle}</span>
            </div>
        )
    }

    return (
        <div
            className={clsx('UiStateView w-full h-full flex items-center justify-center px-6 py-6', className)}
            role={role}
            aria-live={ariaLive}
        >
            <div className="max-w-xl text-center space-y-2">
                {!hideIcon && (
                    <div className="mx-auto mb-2 flex items-center justify-center">
                        {resolvedIcon}
                    </div>
                )}
                <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {resolvedTitle}
                </div>
                {description && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {description}
                    </div>
                )}
                {action && <div className="pt-2">{action}</div>}
            </div>
        </div>
    )
}

export default UiStateView